import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  SwapHoriz as TransferIcon,
  DeleteOutline as DeleteIcon,
  RateReview as ReviewIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import debounce from 'lodash/debounce';
import { useNavigate, useParams } from 'react-router-dom';
import { useVenue } from '../../../../context/VenueContext';
import transferService from '../../../../services/transferService';
import inventoryItemService, { type InventoryItemWithBalance } from '../../../../services/inventoryItemService';
import venueLocationService from '../../../../services/venueLocationService';
import type { Transfer, TransferLine } from '../../../../types/venue';

// ── Helpers ───────────────────────────────────────────────────────────────────

const formatBottleSize = (ml?: number | null): string => {
  if (!ml) return '';
  return ml >= 1000 ? `${ml / 1000}L` : `${ml}ml`;
};

const formatItemSize = (item: { sizeValue?: number | null; sizeUnit?: string | null }): string => {
  if (item.sizeValue != null) return `${item.sizeValue} ${item.sizeUnit ?? 'ml'}`;
  return '';
};

const formatDate = (d: string | null | undefined): string => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
};

// ── Component ─────────────────────────────────────────────────────────────────

const TransferEntry: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { venueId, venueName } = useVenue();
  const { transferId } = useParams<{ transferId: string }>();
  const backPath = `/admin/venues/${venueId}/transfers`;
  const reviewPath = `/admin/venues/${venueId}/transfers/${transferId}/review`;

  // ── Core data ─────────────────────────────────────────────────────────────
  const [transfer, setTransfer] = useState<Transfer | null>(null);
  const [lines, setLines] = useState<TransferLine[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  // ── Resolved location names (independent of line snapshots) ──────────────
  const [fromLocationName, setFromLocationName] = useState<string | null>(null);
  const [toLocationName, setToLocationName] = useState<string | null>(null);

  // ── Header edit (draft only) ──────────────────────────────────────────────
  const [headerForm, setHeaderForm] = useState({ transferredAt: '', transferredBy: '', notes: '' });
  const [headerDirty, setHeaderDirty] = useState(false);
  const [headerSaving, setHeaderSaving] = useState(false);
  const [headerError, setHeaderError] = useState<string | null>(null);

  // ── Search & add line ─────────────────────────────────────────────────────
  const [searchValue, setSearchValue] = useState<InventoryItemWithBalance | null>(null);
  const [searchInput, setSearchInput] = useState('');
  const [searchOptions, setSearchOptions] = useState<InventoryItemWithBalance[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [pendingQty, setPendingQty] = useState<string>('1');
  const [addingLine, setAddingLine] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);

  // ── Per-line editing ──────────────────────────────────────────────────────
  const [localQtys, setLocalQtys] = useState<Record<string, string>>({});
  const [savingLines, setSavingLines] = useState<Set<string>>(new Set());
  const [lineError, setLineError] = useState<string | null>(null);
  const [removingLines, setRemovingLines] = useState<Set<string>>(new Set());

  // ── Load ──────────────────────────────────────────────────────────────────
  const loadTransfer = useCallback(async () => {
    if (!venueId || !transferId) return;
    setLoading(true);
    setLoadError(null);
    try {
      const { transfer: t, lines: l } = await transferService.getTransfer(venueId, transferId);
      setTransfer(t);
      setLines(l);
      setHeaderForm({
        transferredAt: t.transferredAt ? t.transferredAt.split('T')[0] : '',
        transferredBy: t.transferredBy ?? '',
        notes: t.notes ?? '',
      });
      const initial: Record<string, string> = {};
      l.forEach(line => { initial[line._id] = String(line.quantity); });
      setLocalQtys(initial);
    } catch (err: any) {
      setLoadError(err?.response?.data?.error?.message ?? err?.message ?? 'Failed to load transfer');
    } finally {
      setLoading(false);
    }
  }, [venueId, transferId]);

  useEffect(() => { loadTransfer(); }, [loadTransfer]);

  // ── Resolve location names on mount (non-blocking, non-fatal) ────────────
  useEffect(() => {
    if (!transfer || !venueId) return;
    if (transfer.fromLocationId) {
      venueLocationService.getLocation(venueId, transfer.fromLocationId)
        .then(loc => setFromLocationName(loc.name))
        .catch(() => {});
    }
    if (transfer.toLocationId) {
      venueLocationService.getLocation(venueId, transfer.toLocationId)
        .then(loc => setToLocationName(loc.name))
        .catch(() => {});
    }
  }, [transfer?.fromLocationId, transfer?.toLocationId, venueId]);

  // ── Header save ───────────────────────────────────────────────────────────
  const saveHeader = async () => {
    if (!transfer || !transferId || !venueId || !headerDirty) return;
    setHeaderSaving(true);
    setHeaderError(null);
    try {
      const updated = await transferService.updateTransfer(venueId, transferId, {
        transferredAt: headerForm.transferredAt || null,
        transferredBy: headerForm.transferredBy,
        notes: headerForm.notes || undefined,
      });
      setTransfer(updated);
      setHeaderDirty(false);
    } catch (err: any) {
      setHeaderError(err?.response?.data?.error?.message ?? 'Failed to save details');
    } finally {
      setHeaderSaving(false);
    }
  };

  // ── Item search ───────────────────────────────────────────────────────────
  const debouncedSearch = useRef(
    debounce(async (q: string, locId: string | null) => {
      if (q.trim().length < 2) { setSearchOptions([]); setSearchLoading(false); return; }
      setSearchLoading(true);
      try {
        const result = await inventoryItemService.getItems(
          venueId, 1, 20, 'displayName', 'asc', q,
          locId !== null ? locId : undefined,
        );
        setSearchOptions(result.data);
      } catch {
        setSearchOptions([]);
      } finally {
        setSearchLoading(false);
      }
    }, 300),
  ).current;

  useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch]);

  const handleSearchInput = (_: React.SyntheticEvent, value: string) => {
    setSearchInput(value);
    if (value.trim().length < 2) { setSearchOptions([]); setSearchLoading(false); debouncedSearch.cancel(); return; }
    setSearchLoading(true);
    debouncedSearch(value, transfer?.fromLocationId ?? null);
  };

  const handleAddLine = async () => {
    if (!searchValue || !transferId || !venueId) return;
    const qty = parseInt(pendingQty, 10);
    if (!Number.isInteger(qty) || qty < 1) {
      setAddError('Quantity must be a positive integer.');
      return;
    }
    setAddingLine(true);
    setAddError(null);
    try {
      const newLine = await transferService.addLine(venueId, transferId, searchValue._id, qty);
      setLines(prev => [...prev, newLine]);
      setLocalQtys(prev => ({ ...prev, [newLine._id]: String(newLine.quantity) }));
      setSearchValue(null);
      setSearchInput('');
      setSearchOptions([]);
      setPendingQty('1');
    } catch (err: any) {
      setAddError(err?.response?.data?.error?.message ?? err?.message ?? 'Failed to add item');
    } finally {
      setAddingLine(false);
    }
  };

  // ── Per-line editing ──────────────────────────────────────────────────────
  const handleQtyBlur = async (lineId: string) => {
    if (!venueId || !transferId) return;
    const raw = localQtys[lineId] ?? '';
    const num = parseInt(raw, 10);
    if (!Number.isInteger(num) || num < 1) {
      setLineError('Quantity must be a positive integer.');
      return;
    }
    const line = lines.find(l => l._id === lineId);
    if (!line || num === line.quantity) return;

    setSavingLines(prev => new Set(prev).add(lineId));
    try {
      const updated = await transferService.updateLine(venueId, transferId, lineId, num);
      setLines(prev => prev.map(l => l._id === lineId ? { ...l, quantity: updated.quantity } : l));
    } catch (err: any) {
      setLineError(err?.response?.data?.error?.message ?? 'Failed to save quantity');
      setLocalQtys(prev => ({ ...prev, [lineId]: String(line.quantity) })); // revert
    } finally {
      setSavingLines(prev => { const s = new Set(prev); s.delete(lineId); return s; });
    }
  };

  const handleRemoveLine = async (lineId: string) => {
    if (!venueId || !transferId) return;
    setRemovingLines(prev => new Set(prev).add(lineId));
    try {
      await transferService.removeLine(venueId, transferId, lineId);
      setLines(prev => prev.filter(l => l._id !== lineId));
      setLocalQtys(prev => { const n = { ...prev }; delete n[lineId]; return n; });
    } catch (err: any) {
      setLineError(err?.response?.data?.error?.message ?? 'Failed to remove line');
      setRemovingLines(prev => { const s = new Set(prev); s.delete(lineId); return s; });
    }
  };

  // ── Derived ───────────────────────────────────────────────────────────────
  const isDraft = transfer?.status === 'draft';

  // Resolved from venue locations API — never falls back to raw ObjectIds.
  // Shows '…' while the async fetch is in flight.
  const locationLabel = (id: string | null | undefined): string => {
    if (!id) return `${venueName} (General)`;
    if (id === transfer?.fromLocationId) return fromLocationName ?? '…';
    if (id === transfer?.toLocationId) return toLocationName ?? '…';
    return '…';
  };

  const scopeLabel = transfer
    ? `${locationLabel(transfer.fromLocationId)} → ${locationLabel(transfer.toLocationId)}`
    : '';

  if (loading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', pt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (loadError || !transfer) {
    return (
      <Box sx={{ p: 3 }}>
        <Button startIcon={<BackIcon />} onClick={() => navigate(backPath)} sx={{ mb: 2, textTransform: 'none', color: 'text.secondary' }}>
          Transfers
        </Button>
        <Alert severity="error">{loadError ?? 'Transfer not found'}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2.5 }}>

      {/* Nav bar */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            startIcon={<BackIcon />}
            onClick={() => navigate(backPath)}
            sx={{ textTransform: 'none', color: 'text.secondary' }}
          >
            Transfers
          </Button>
          <Divider orientation="vertical" flexItem sx={{ my: 0.5 }} />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TransferIcon sx={{ color: theme.palette.primary.main, fontSize: 22 }} />
            <Typography variant="h6" fontWeight={600}>{scopeLabel}</Typography>
          </Box>
          <Chip
            label={transfer.status === 'posted' ? 'Posted' : 'Draft'}
            size="small"
            color={transfer.status === 'posted' ? 'success' : 'default'}
          />
        </Box>
        {isDraft && (
          <Button
            variant="outlined"
            startIcon={<ReviewIcon />}
            onClick={() => navigate(reviewPath)}
            sx={{ textTransform: 'none' }}
          >
            Review & Post Transfer
          </Button>
        )}
      </Box>

      {/* Header details panel */}
      <Paper sx={{ p: 2.5, borderRadius: 2, boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
        {isDraft ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="subtitle2" fontWeight={600} color="text.secondary">Transfer Details</Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <TextField
                label="Transfer Date"
                type="date"
                value={headerForm.transferredAt}
                onChange={e => { setHeaderForm(f => ({ ...f, transferredAt: e.target.value })); setHeaderDirty(true); }}
                size="small"
                sx={{ width: 180 }}
                slotProps={{ inputLabel: { shrink: true } }}
                helperText="When the physical transfer happened"
              />
              <TextField
                label="Transferred By"
                value={headerForm.transferredBy}
                onChange={e => { setHeaderForm(f => ({ ...f, transferredBy: e.target.value })); setHeaderDirty(true); }}
                size="small"
                sx={{ width: 200 }}
              />
              <TextField
                label="Notes"
                value={headerForm.notes}
                onChange={e => { setHeaderForm(f => ({ ...f, notes: e.target.value })); setHeaderDirty(true); }}
                size="small"
                sx={{ flex: 1, minWidth: 200 }}
                placeholder="Optional"
              />
            </Box>
            {headerError && <Alert severity="error" sx={{ py: 0.5 }}>{headerError}</Alert>}
            {headerDirty && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={headerSaving ? <CircularProgress size={14} /> : <SaveIcon />}
                  disabled={headerSaving}
                  onClick={saveHeader}
                  sx={{ textTransform: 'none' }}
                >
                  {headerSaving ? 'Saving…' : 'Save Transfer'}
                </Button>
              </Box>
            )}
          </Box>
        ) : (
          <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            <Box>
              <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase' }}>Route</Typography>
              <Typography variant="body2" fontWeight={500}>{scopeLabel}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase' }}>Transfer Date</Typography>
              <Typography variant="body2">{formatDate(transfer.transferredAt)}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase' }}>Transferred By</Typography>
              <Typography variant="body2">{transfer.transferredBy || '—'}</Typography>
            </Box>
            {transfer.postedAt && (
              <Box>
                <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase' }}>Posted</Typography>
                <Typography variant="body2">{formatDate(transfer.postedAt)}</Typography>
              </Box>
            )}
            {transfer.notes && (
              <Box>
                <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase' }}>Notes</Typography>
                <Typography variant="body2">{transfer.notes}</Typography>
              </Box>
            )}
          </Box>
        )}
      </Paper>

      {/* Add items panel (draft only) */}
      {isDraft && (
        <Paper sx={{ p: 2.5, borderRadius: 2, boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
          <Typography variant="subtitle2" fontWeight={600} color="text.secondary" sx={{ mb: 1.5 }}>
            Add Items from{' '}
            <Box component="span" sx={{ color: theme.palette.primary.main }}>
              {locationLabel(transfer.fromLocationId)}
            </Box>
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap' }}>
            <Autocomplete
              value={searchValue}
              inputValue={searchInput}
              options={searchOptions}
              loading={searchLoading}
              filterOptions={x => x}
              getOptionLabel={opt => opt.displayName}
              isOptionEqualToValue={(a, b) => a._id === b._id}
              onInputChange={handleSearchInput}
              onChange={(_, newVal) => { setSearchValue(newVal); }}
              noOptionsText={searchInput.length < 2 ? 'Type at least 2 characters' : 'No items found in this location'}
              sx={{ flex: 1, minWidth: 280 }}
              renderOption={(props, option) => {
                const { key, ...rest } = props as React.HTMLAttributes<HTMLLIElement> & { key: React.Key };
                const avail = option.balance?.totalQuantity ?? 0;
                return (
                  <li key={key} {...rest}>
                    <Box sx={{ py: 0.5, width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2" fontWeight={500}>
                        {option.displayName}
                      </Typography>
                      <Typography variant="caption" color={avail <= 0 ? 'error' : 'text.secondary'} sx={{ ml: 2, whiteSpace: 'nowrap' }}>
                        {avail} in stock
                      </Typography>
                    </Box>
                  </li>
                );
              }}
              renderInput={params => (
                <TextField
                  {...params}
                  label="Search items in source location"
                  size="small"
                  slotProps={{
                    input: {
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {searchLoading && <CircularProgress color="inherit" size={16} sx={{ mr: 0.5 }} />}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    },
                  }}
                />
              )}
            />
            <TextField
              label="Qty"
              type="number"
              value={pendingQty}
              onChange={e => setPendingQty(e.target.value)}
              size="small"
              sx={{ width: 80 }}
              slotProps={{ htmlInput: { min: 1, step: 1 } }}
            />
            <Button
              variant="contained"
              disabled={!searchValue || addingLine}
              onClick={handleAddLine}
              sx={{ textTransform: 'none', height: 40, whiteSpace: 'nowrap' }}
              startIcon={addingLine ? <CircularProgress size={14} color="inherit" /> : undefined}
            >
              {addingLine ? 'Adding…' : 'Add Line'}
            </Button>
          </Box>
          {addError && (
            <Alert severity="error" sx={{ mt: 1.5, py: 0.5 }} onClose={() => setAddError(null)}>
              {addError}
            </Alert>
          )}
        </Paper>
      )}

      {/* Lines table */}
      <Paper sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
        <Box sx={{
          px: 3, py: 2,
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <Box>
            <Typography variant="subtitle1" fontWeight={600}>Transfer Lines</Typography>
            <Typography variant="caption" color="text.secondary">
              {lines.length} item{lines.length !== 1 ? 's' : ''}
            </Typography>
          </Box>
        </Box>

        {lineError && (
          <Box sx={{ px: 3, pt: 2 }}>
            <Alert severity="error" onClose={() => setLineError(null)}>{lineError}</Alert>
          </Box>
        )}

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                {['Item', 'Size', 'From', 'To', 'In Stock', 'Qty to Transfer', ''].map(h => (
                  <TableCell
                    key={h}
                    sx={{ fontWeight: 600, color: theme.palette.text.secondary, fontSize: '0.7rem', textTransform: 'uppercase', py: 1.25, whiteSpace: 'nowrap' }}
                  >
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {lines.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7}>
                    <Box sx={{ py: 6, textAlign: 'center', color: theme.palette.text.secondary }}>
                      <TransferIcon sx={{ fontSize: 40, mb: 1, opacity: 0.3 }} />
                      <Typography variant="body2">
                        {isDraft
                          ? 'No items yet. Search above to add items from the source location.'
                          : 'No lines on this transfer.'}
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                lines.map(line => {
                  const isSaving = savingLines.has(line._id);
                  const isRemoving = removingLines.has(line._id);

                  return (
                    <TableRow key={line._id} sx={{ opacity: isRemoving ? 0.4 : 1 }}>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>
                          {line.snapshot.displayName}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {formatBottleSize(line.snapshot.bottleSize)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {line.snapshot.fromLocationName ?? `${venueName} (General)`}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {line.snapshot.toLocationName ?? `${venueName} (General)`}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {line.currentQty !== undefined ? (
                          <Typography
                            variant="body2"
                            color={line.currentQty <= 0 ? 'error' : 'text.secondary'}
                          >
                            {line.currentQty}
                          </Typography>
                        ) : (
                          <Typography variant="body2" color="text.disabled">—</Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ minWidth: 100, py: 0.75 }}>
                        {isDraft ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <TextField
                              value={localQtys[line._id] ?? ''}
                              onChange={e => { setLocalQtys(prev => ({ ...prev, [line._id]: e.target.value })); setLineError(null); }}
                              onBlur={() => handleQtyBlur(line._id)}
                              type="number"
                              size="small"
                              sx={{ width: 90, '& input': { textAlign: 'center', fontWeight: 600 } }}
                              slotProps={{ htmlInput: { min: 1, step: 1 } }}
                            />
                            {isSaving && <CircularProgress size={14} />}
                          </Box>
                        ) : (
                          <Typography variant="body2" fontWeight={600}>{line.quantity}</Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {isDraft && (
                          <Tooltip title="Remove line">
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => handleRemoveLine(line._id)}
                                disabled={isRemoving}
                                sx={{ color: theme.palette.text.disabled, '&:hover': { color: 'error.main' } }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Footer — draft only, only when lines exist */}
      {isDraft && lines.length > 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5 }}>
          <Button onClick={() => navigate(backPath)} sx={{ textTransform: 'none' }}>
            Back to List
          </Button>
          <Button
            variant="contained"
            startIcon={<ReviewIcon />}
            onClick={() => navigate(reviewPath)}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            Review & Post Transfer
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default TransferEntry;
